import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SEO from '../components/SEO';
import { Loader2, Eye, EyeOff, Leaf } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.company_name}!`);
      
      // Redirect based on role
      if (user.role === 'pro_admin') {
        navigate('/admin');
      } else {
        navigate('/app');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <SEO 
        title="Sign In | AfriCycle Zambia"
        description="Sign in to your AfriCycle Zambia account to manage your EPR compliance."
        canonicalUrl="https://africycle.pro/login"
        noindex={true}
      />
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#D2E4D6] mb-4">
              <Leaf className="h-6 w-6 text-[#056839]" />
            </div>
            <h1 className="text-3xl font-bold text-[#056839]">WELCOME BACK</h1>
            <p className="text-[#6B7280] mt-2">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#3F3F3F]">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-12"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#3F3F3F]">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#3F3F3F]"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-[#6B7280] mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#056839] font-medium hover:underline" data-testid="register-link">
              Create Account
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div 
        className="hidden lg:flex flex-1 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1512428813834-c702c7702b78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwb2ZmaWNlJTIwZGVzayUyMHBsYW50fGVufDB8fHx8MTc3MjQ2MzQxNnww&ixlib=rb-4.1.0&q=85)' 
        }}
      >
        <div className="absolute inset-0 bg-[#056839]/60" />
        <div className="relative z-10 p-12 flex flex-col justify-end text-white">
          <h2 className="text-3xl font-bold mb-4">Simplify Your Compliance Journey</h2>
          <p className="text-white/80 text-lg">
            Join producers who trust AfriCycle Zambia for their plastic packaging and WEEE EPR compliance needs.
          </p>
        </div>
      </div>
    </div>
  );
}
